/**
 * Iter 84.3 / Phase E5 Sprint 3 — TDS Challans CRUD page.
 *
 * D36-E enforcement is server-side; this UI shows the "locked"
 * indicator on rows referenced by a generated Form 24Q and disables
 * the Edit / Delete actions accordingly.
 */
import { useCallback, useEffect, useState } from "react";
import {
    Plus, Trash, ArrowsClockwise, Lock, Pencil,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

const fmtMoney = (v) =>
    v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const STATUS_COLORS = {
    Deposited: "bg-amber-100 text-amber-700",
    Reconciled: "bg-emerald-100 text-emerald-700",
    Filed: "bg-blue-100 text-blue-700",
};

const EMPTY_FORM = {
    financial_year: "2025-2026",
    period_label: "2025-04",
    bsr_code: "",
    challan_serial_no: "",
    challan_date: "",
    amount_deposited: "",
    section_code: "192",
    note: "",
};

export default function TdsChallansPage() {
    const { can } = useAuth();
    const canAdmin = can("compliance:admin");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fyFilter, setFyFilter] = useState("2025-2026");
    const [quarterFilter, setQuarterFilter] = useState("");
    // Editor dialog
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    const reload = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (fyFilter) params.set("financial_year", fyFilter);
            if (quarterFilter) params.set("quarter", quarterFilter);
            const { data } = await api.get(`/compliance/tds-challans?${params}`);
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load challans"));
        }
    }, [fyFilter, quarterFilter]);

    useEffect(() => { reload(); }, [reload]);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setOpen(true);
    };

    const openEdit = (row) => {
        if ((row.locked_in_filing_ids || []).length > 0) {
            toast.error("This challan is locked — referenced by a generated Form 24Q filing.");
            return;
        }
        setEditing(row);
        setForm({
            financial_year: row.financial_year,
            period_label: row.period_label,
            bsr_code: row.bsr_code,
            challan_serial_no: row.challan_serial_no,
            challan_date: row.challan_date,
            amount_deposited: String(row.amount_deposited),
            section_code: row.section_code,
            note: row.note || "",
        });
        setOpen(true);
    };

    const handleSave = async () => {
        setLoading(true);
        const body = {
            ...form,
            amount_deposited: parseFloat(form.amount_deposited || "0"),
        };
        try {
            if (editing) {
                await api.put(`/compliance/tds-challans/${editing.id}`, body);
                toast.success(`Challan ${editing.id} updated`);
            } else {
                const { data } = await api.post("/compliance/tds-challans", body);
                toast.success(`Challan created · CIN ${data.cin}`);
            }
            setOpen(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (row) => {
        if (!window.confirm(`Delete challan ${row.cin}?`)) return;
        setLoading(true);
        try {
            await api.delete(`/compliance/tds-challans/${row.id}`);
            toast.success("Challan deleted");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 space-y-4" data-testid="tds-challans-page">
            <PageHeader
                title="TDS Challans"
                subtitle="Bank receipts for monthly TDS deposits · D36-E — locked once referenced by a generated Form 24Q"
                actions={
                    <>
                        <Button size="sm" variant="outline" onClick={reload} data-testid="tds-challans-refresh-btn">
                            <ArrowsClockwise size={14} className="mr-1" /> Refresh
                        </Button>
                        {canAdmin && (
                            <Button size="sm" onClick={openCreate} data-testid="tds-challan-new-btn" className="ml-2">
                                <Plus size={14} className="mr-1" /> New Challan
                            </Button>
                        )}
                    </>
                }
            />

            <div className="flex gap-3 items-end">
                <div>
                    <Label className="text-xs">FY</Label>
                    <Input value={fyFilter} onChange={(e) => setFyFilter(e.target.value)} className="font-mono w-32" data-testid="tds-challan-fy-filter" />
                </div>
                <div>
                    <Label className="text-xs">Quarter</Label>
                    <select value={quarterFilter} onChange={(e) => setQuarterFilter(e.target.value)} className="border rounded px-2 py-1 text-sm" data-testid="tds-challan-quarter-filter">
                        <option value="">All</option><option value="Q1">Q1</option><option value="Q2">Q2</option><option value="Q3">Q3</option><option value="Q4">Q4</option>
                    </select>
                </div>
                <div className="text-xs text-slate-500 ml-auto">{items.length} challan(s)</div>
            </div>

            <div className="border rounded">
                <table className="w-full text-xs" data-testid="tds-challans-table">
                    <thead className="bg-slate-50 border-b uppercase text-slate-600">
                        <tr>
                            <th className="text-left px-2 py-1">CIN</th>
                            <th className="text-left px-2 py-1">FY · Period</th>
                            <th className="text-left px-2 py-1">Date</th>
                            <th className="text-right px-2 py-1">Amount</th>
                            <th className="text-left px-2 py-1">Section</th>
                            <th className="text-left px-2 py-1">Status</th>
                            <th className="text-left px-2 py-1">Locked In</th>
                            <th className="text-left px-2 py-1">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan="8" className="text-center text-slate-400 p-6">No challans yet</td></tr>
                        )}
                        {items.map((row) => {
                            const locked = (row.locked_in_filing_ids || []).length > 0;
                            return (
                                <tr key={row.id} className="border-b hover:bg-slate-50/50" data-testid={`tds-challan-row-${row.id}`}>
                                    <td className="px-2 py-1 font-mono text-[11px]">{row.cin}</td>
                                    <td className="px-2 py-1">{row.financial_year} · {row.period_label}</td>
                                    <td className="px-2 py-1 font-mono text-[11px]">{row.challan_date}</td>
                                    <td className="px-2 py-1 text-right font-mono">{fmtMoney(row.amount_deposited)}</td>
                                    <td className="px-2 py-1 font-mono">{row.section_code}</td>
                                    <td className="px-2 py-1"><Badge className={STATUS_COLORS[row.status] || ""}>{row.status}</Badge></td>
                                    <td className="px-2 py-1 text-[10px]">
                                        {locked ? (
                                            <span className="inline-flex items-center gap-1 text-blue-700">
                                                <Lock size={10} /> {(row.locked_in_filing_ids || []).length} filing(s)
                                            </span>
                                        ) : <span className="text-slate-400">—</span>}
                                    </td>
                                    <td className="px-2 py-1">
                                        {canAdmin && !locked && (
                                            <>
                                                <Button size="sm" variant="outline" onClick={() => openEdit(row)} data-testid={`tds-challan-edit-${row.id}`}>
                                                    <Pencil size={12} />
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => handleDelete(row)} data-testid={`tds-challan-delete-${row.id}`} className="ml-1 text-rose-600">
                                                    <Trash size={12} />
                                                </Button>
                                            </>
                                        )}
                                        {locked && (
                                            <span className="text-[10px] text-blue-700">D36-E locked</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>{editing ? `Edit Challan · ${editing.cin}` : "New TDS Challan"}</DialogTitle>
                        <DialogDescription className="text-xs">
                            CIN uniqueness: BSR code + challan date + serial no must be unique across the system.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                            <Label className="text-xs">FY</Label>
                            <Input value={form.financial_year} onChange={(e) => setForm({ ...form, financial_year: e.target.value })} className="font-mono" data-testid="tds-challan-fy-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Period (YYYY-MM)</Label>
                            <Input value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} className="font-mono" data-testid="tds-challan-period-input" />
                        </div>
                        <div>
                            <Label className="text-xs">BSR Code (7 digits)</Label>
                            <Input value={form.bsr_code} onChange={(e) => setForm({ ...form, bsr_code: e.target.value })} className="font-mono" maxLength={7} data-testid="tds-challan-bsr-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Challan Serial No.</Label>
                            <Input value={form.challan_serial_no} onChange={(e) => setForm({ ...form, challan_serial_no: e.target.value })} className="font-mono" data-testid="tds-challan-serial-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Challan Date (YYYY-MM-DD)</Label>
                            <Input type="date" value={form.challan_date} onChange={(e) => setForm({ ...form, challan_date: e.target.value })} className="font-mono" data-testid="tds-challan-date-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Amount Deposited (₹)</Label>
                            <Input type="number" min="0" step="0.01" value={form.amount_deposited} onChange={(e) => setForm({ ...form, amount_deposited: e.target.value })} className="font-mono" data-testid="tds-challan-amount-input" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Section Code</Label>
                            <select
                                value={form.section_code}
                                onChange={(e) => setForm({ ...form, section_code: e.target.value })}
                                className="font-mono w-32 border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white"
                                data-testid="tds-challan-section-input"
                            >
                                {/* Iter 111.1 · obs #8 — canonical TDS section codes */}
                                <option value="192">192 — Salary</option>
                                <option value="192A">192A — Premature EPF withdrawal</option>
                                <option value="193">193 — Interest on securities</option>
                                <option value="194">194 — Dividends</option>
                                <option value="194A">194A — Interest other than securities</option>
                                <option value="194B">194B — Winnings from lottery/games</option>
                                <option value="194C">194C — Payments to contractors</option>
                                <option value="194H">194H — Commission &amp; brokerage</option>
                                <option value="194I">194I — Rent</option>
                                <option value="194IA">194IA — Immovable property purchase</option>
                                <option value="194IB">194IB — Rent (individual/HUF)</option>
                                <option value="194J">194J — Professional / technical fees</option>
                                <option value="194K">194K — Income from mutual fund units</option>
                                <option value="194N">194N — Cash withdrawals</option>
                                <option value="194O">194O — E-commerce operators</option>
                                <option value="194Q">194Q — Purchase of goods</option>
                                <option value="194R">194R — Benefits &amp; perquisites</option>
                                <option value="194S">194S — Transfer of VDAs</option>
                                <option value="195">195 — Payments to non-residents</option>
                                <option value="196A">196A — Foreign company income from units</option>
                                <option value="197">197 — Lower/nil deduction certificate</option>
                            </select>
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Note (optional)</Label>
                            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="tds-challan-note-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={loading} data-testid="tds-challan-save-btn">
                            {editing ? "Save Changes" : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
